import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import PropTypes from 'prop-types'

const DEFAULT_FORM = {
  name: '',
  type: 'school',
  domain: ''
}

const CUSTOM_TYPE = '__custom__'

/**
 * Create / edit an organization (super admin). When `organization` is provided
 * the modal is in edit mode and the slug is shown read-only (never editable).
 *
 * `existingTypes` seeds the Type dropdown so admins can reuse known types
 * (school, family, ...); "Other (custom)…" reveals a free-text field for a new
 * type. (A styled <select> is used rather than a native <datalist>, whose popup
 * the browser renders unstyled and overlapping in dark mode.)
 */
const OrganizationFormModal = ({ isOpen, onClose, onSubmit, organization, existingTypes = [] }) => {
  const [form, setForm] = useState(DEFAULT_FORM)
  const [submitting, setSubmitting] = useState(false)
  // True when the admin is entering a type not in the known list.
  const [customType, setCustomType] = useState(false)

  const isEdit = Boolean(organization)

  const typeOptions = useMemo(() => [...new Set(['school', ...existingTypes])], [existingTypes])

  useEffect(() => {
    if (isOpen) {
      if (organization) {
        setForm({
          name: organization.name || '',
          type: organization.type || '',
          domain: organization.domain || ''
        })
        // If the org's existing type isn't a known option, edit it as custom.
        setCustomType(Boolean(organization.type) && !typeOptions.includes(organization.type))
      } else {
        setForm(DEFAULT_FORM)
        setCustomType(false)
      }
      setSubmitting(false)
    }
  }, [isOpen, organization, typeOptions])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.type.trim()) return
    setSubmitting(true)
    try {
      await onSubmit({
        name: form.name.trim(),
        type: form.type.trim(),
        domain: form.domain.trim() || null
      })
    } catch {
      // Error handling is done in the parent (toast)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isEdit ? 'Edit Organization' : 'Create Organization'}
              </h2>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  maxLength={200}
                  placeholder="e.g. Lincoln High School"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={customType ? CUSTOM_TYPE : form.type}
                  onChange={(e) => {
                    if (e.target.value === CUSTOM_TYPE) {
                      setCustomType(true)
                      handleChange('type', '')
                    } else {
                      setCustomType(false)
                      handleChange('type', e.target.value)
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value={CUSTOM_TYPE}>Other (custom)…</option>
                </select>
                {customType && (
                  <input
                    type="text"
                    value={form.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    required
                    autoFocus
                    placeholder="Enter a custom type"
                    className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                )}
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email domain <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.domain}
                  onChange={(e) => handleChange('domain', e.target.value)}
                  placeholder="lincolnhigh.edu"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {isEdit && organization?.slug && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Slug: <span className="font-mono">{organization.slug}</span> (not editable)
                </p>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !form.name.trim() || !form.type.trim()}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Organization'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

OrganizationFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  organization: PropTypes.object,
  existingTypes: PropTypes.arrayOf(PropTypes.string)
}

export default OrganizationFormModal
